import { saveAs } from "file-saver";
import React, { useCallback, useState } from "react";
import { browserName } from "react-device-detect";
import { useTranslation } from "react-i18next";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import Icons from "../../utils/components/icons";
import { formatDate, trimText } from "../../utils/utils";

const DeviceSetup = ({ selectedDevice }) => {
  const { t } = useTranslation("serviceTool");

  const [fileName, setFileName] = useState("");
  const [fileContent, setFileContent] = useState("");
  const [fileLoadedTime, setFileLoadedTime] = useState("");

  const [infoModal, setInfoModal] = useState({
    open: false,
    msg: "",
    translated: false,
  });

  const onInfoModalClose = useCallback(() => {
    setInfoModal((prev) => ({ ...prev, open: false, msg: "" }));
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (file) {
      if (file.type !== "application/json") {
        setInfoModal((prev) => ({
          ...prev,
          open: true,
          msg: t("setup.invalidFileType"),
        }));
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jsonContent = JSON.parse(reader.result);
          setFileName(file.name);
          setFileContent(JSON.stringify(jsonContent, null, 2));
          setFileLoadedTime(new Date());
        } catch (error) {
          setInfoModal((prev) => ({
            ...prev,
            open: true,
            msg: t("setup.unableToLoad"),
          }));
          e.target.value = "";
        }
      };
      reader.readAsText(file);
    }
  };

  const handleSaveToDevice = () => {
    console.log("Saving to device:", fileContent);
  };

  const handleFileRemove = () => {
    setFileName("");
    setFileContent("");
    setFileLoadedTime("");
  };

  const handleSaveAs = useCallback(async () => {
    try {
      if (browserName === "Chrome" || browserName === "Edge") {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: "Chorus_device_setup.json",
          types: [
            {
              description: "JSON File",
              accept: { "application/json": [".json"] },
            },
          ],
        });

        const writableStream = await fileHandle.createWritable();
        await writableStream.write(fileContent, null, 2);
        await writableStream.close();
      } else {
        const blob = new Blob([fileContent], {
          type: "application/json",
        });
        saveAs(blob, "Chorus_device_setup.json");
      }
    } catch (err) {
      console.error("Save failed:", err);
    }
  }, [fileContent]);

  const loadFromDevice = useCallback(() => {
    // will cover once API is ready
  }, []);

  return (
    <div className="device-setup-tab">
      <div className="upper-section">
        <div>
          <span className="heading-h3">{t("setup.uploadJSONTitle")}</span>
        </div>
        <div className="browse-section">
          {!fileName ? (
            <>
              <button id="browse" className="roundBtn browse-btn">
                <label htmlFor="file-upload" className="browse-label">
                  {t("setup.browse")}
                </label>
              </button>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
            </>
          ) : (
            <div className="remove-file roundBtn">
              <div>
                <span>{Icons.jsonFileIcon}</span>
                <span className="file-name" title={fileName}>
                  {trimText(fileName, 16)}
                </span>
              </div>
              <div onClick={handleFileRemove} className="pointer" title={t("setup.remove")}>
                {Icons.xCircle}
              </div>
            </div>
          )}
          <div className="right-section">
            <button
              id="load-from-device"
              className="roundBtn primary"
              onClick={loadFromDevice}
            >
              {Icons.undo}
              <span>{t("setup.loadDeviceConfig")}</span>
            </button>

            <button
              id="save-as"
              className="roundBtn primary"
              onClick={handleSaveAs}
              disabled={!fileContent}
            >
              {Icons.saveAs}
              <span>{t("setup.saveAs")}</span>
            </button>
          </div>
        </div>
      </div>
      <div className={`lower-section ${fileContent ? "logs" : ""}`}>
        {fileContent ? (
          <div className="logs-subdiv">
            <div className="log-output-section">
              <textarea
                className="heading-h3"
                value={fileContent}
                readOnly
              ></textarea>
            </div>

            <div className="footer-section">
              <p className="text-h4 file-details">
                Loaded from file{" "}
                <span className="text-h4" title={fileName}>
                  {trimText(fileName, 31)}
                </span>{" "}
                on {formatDate(new Date(fileLoadedTime), false, false)}
              </p>

              <button
                id="save-to-device"
                className="roundBtn primary"
                onClick={handleSaveToDevice}
                disabled={true}
              >
                <span>{t("setup.saveToDevice")}</span>
              </button>
            </div>
          </div>
        ) : (
          <EmptyScreen
            img={Icons.jsonUpload}
            small={true}
            title={t("setup.uploadJSON")}
            desc={""}
          />
        )}
      </div>
      {/* Err/Success Modal */}
      <ModalComponent
        idName="setup-info-modal"
        isOpen={infoModal.open}
        onCloseModal={onInfoModalClose}
        descriptionText={infoModal.msg}
      />
    </div>
  );
};

export default DeviceSetup;
